// pdf.js — ANTEPRIMA PDF di cortesia della fattura (stile classico "copia di
// cortesia" italiano, alla Aruba fatturazione elettronica).
//
// API: buildFatturaPdf(jsPDFCtor, record, opts) -> istanza jsPDF pronta
// (il chiamante fa .output()/.save()). Il costruttore jsPDF arriva dal
// chiamante (window.jspdf.jsPDF, self-hosted): questo modulo NON lo importa.
//
// record = shape del CONTRATTO MVP (docs/fatturapa/CONTRATTO-MVP.md):
// riepiloghi e totali sono GIÀ calcolati dal server, qui si stampa e basta.
// opts: { titolo?: 'ANTEPRIMA BOZZA' } — se la fattura è in stato 'bozza'
// (o se opts.titolo è passato) si stampano badge + filigrana
// 'ANTEPRIMA — NON VALIDA AI FINI FISCALI'.
//
// Solo jsPDF base: text / line / rect / addPage / setPage / splitTextToSize,
// font helvetica. NIENTE plugin (no autotable).

// ————————————————————————— COSTANTI DI LAYOUT (mm, A4 verticale) —————————————

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 12;                       // margine su tutti i lati
const CONTENT_W = PAGE_W - 2 * MARGIN;   // 186
const FOOTER_H = 10;                     // fascia riservata al piè di pagina
const BOTTOM_Y = PAGE_H - MARGIN - FOOTER_H; // limite inferiore del contenuto

const LINE_H = 4.0;      // interlinea corpo (font 8)
const LINE_H_SMALL = 3.4; // interlinea note piccole (font 7)
const BOX_PAD = 2.2;     // padding interno dei riquadri
const BAND_H = 5.4;      // altezza banda titolo dei riquadri
const TABLE_HEAD_H = 6;  // altezza header tabella righe
const ROW_PAD_V = 1.5;   // padding verticale celle tabella
const GAP = 4;           // spazio verticale tra i blocchi

const FS = {             // font size (pt)
  band: 8, label: 6.3, body: 8, small: 7, foot: 6.8,
  totaleFinale: 9, badge: 8, watermark: 27,
};

const GRAY_BAND = 228;   // riempimento bande titolo
const GRAY_ZEBRA = 244;  // righe alterne tabella
const GRAY_LABEL = 105;  // testo etichette secondarie
const GRAY_LINE = 160;   // linee sottili
const INK = 25;          // testo principale
const WATERMARK_GRAY = 210;
const BADGE_RGB = [178, 60, 60];

const TOTALI_W = 68;     // larghezza blocco totali a destra
const RIEP_W = CONTENT_W - TOTALI_W - GAP; // riquadro riepiloghi IVA a sinistra

// ————————————————————————— TABELLE DI DECODIFICA —————————————————————————————

const TIPO_DOC = {
  TD01: 'Fattura', TD02: 'Acconto/anticipo su fattura', TD03: 'Acconto/anticipo su parcella',
  TD04: 'Nota di credito', TD05: 'Nota di debito', TD06: 'Parcella',
  TD16: 'Integrazione fattura reverse charge interno',
  TD17: 'Integrazione/autofattura acquisto servizi dall\'estero',
  TD18: 'Integrazione acquisto beni intracomunitari',
  TD19: 'Integrazione/autofattura acquisto beni art.17 c.2',
  TD20: 'Autofattura per regolarizzazione', TD21: 'Autofattura per splafonamento',
  TD22: 'Estrazione beni da deposito IVA', TD23: 'Estrazione beni da deposito IVA con IVA',
  TD24: 'Fattura differita art.21 c.4 lett. a)', TD25: 'Fattura differita art.21 c.4 lett. b)',
  TD26: 'Cessione beni ammortizzabili / passaggi interni', TD27: 'Autoconsumo / cessioni gratuite',
  TD28: 'Acquisti da San Marino con IVA', TD29: 'Comunicazione omessa/irregolare fatturazione',
};
const REGIME = {
  RF01: 'Ordinario', RF02: 'Contribuenti minimi', RF04: 'Agricoltura e attività connesse',
  RF05: 'Vendita sali e tabacchi', RF06: 'Commercio fiammiferi', RF07: 'Editoria',
  RF08: 'Gestione servizi telefonia pubblica', RF09: 'Rivendita documenti di trasporto',
  RF10: 'Intrattenimenti e giochi', RF11: 'Agenzie viaggi e turismo', RF12: 'Agriturismo',
  RF13: 'Vendite a domicilio', RF14: 'Rivendita beni usati, arte, antiquariato',
  RF15: 'Agenzie di vendite all\'asta', RF16: 'IVA per cassa P.A.', RF17: 'IVA per cassa',
  RF18: 'Altro', RF19: 'Regime forfettario',
};
const CONDIZIONI_PAG = { TP01: 'Pagamento a rate', TP02: 'Pagamento completo', TP03: 'Anticipo' };
const MODALITA_PAG = {
  MP01: 'Contanti', MP02: 'Assegno', MP03: 'Assegno circolare', MP04: 'Contanti presso Tesoreria',
  MP05: 'Bonifico', MP06: 'Vaglia cambiario', MP07: 'Bollettino bancario', MP08: 'Carta di pagamento',
  MP09: 'RID', MP10: 'RID utenze', MP11: 'RID veloce', MP12: 'RIBA', MP13: 'MAV',
  MP14: 'Quietanza erario', MP15: 'Giroconto su conti di contabilità speciale',
  MP16: 'Domiciliazione bancaria', MP17: 'Domiciliazione postale', MP18: 'Bollettino c/c postale',
  MP19: 'SEPA Direct Debit', MP20: 'SEPA Direct Debit CORE', MP21: 'SEPA Direct Debit B2B',
  MP22: 'Trattenuta su somme già riscosse', MP23: 'PagoPA',
};
const ESIGIBILITA = { I: 'Immediata', D: 'Differita', S: 'Split payment' };
const RITENUTA_TIPO = {
  RT01: 'Ritenuta persone fisiche', RT02: 'Ritenuta persone giuridiche', RT03: 'Contributo INPS',
  RT04: 'Contributo ENASARCO', RT05: 'Contributo ENPAM', RT06: 'Altro contributo previdenziale',
};

// ————————————————————————— FORMATTAZIONE (stile italiano) ————————————————————

// 1234.5 → '1.234,50' (dec decimali fissi, migliaia col punto)
function fmtNum(x, dec) {
  const n = Number(x);
  if (x == null || x === '' || isNaN(n)) return '';
  const neg = n < 0 ? '-' : '';
  const parts = Math.abs(n).toFixed(dec == null ? 2 : dec).split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return neg + intPart + (parts[1] ? ',' + parts[1] : '');
}
const fmtImporto = (x) => fmtNum(x, 2);

// prezzo unitario: minimo 2 decimali, fino a 8 se il valore li ha davvero
function fmtPrezzo(x) {
  const n = Number(x);
  if (x == null || x === '' || isNaN(n)) return '';
  const s = String(n);
  const i = s.indexOf('.');
  const dec = i < 0 ? 0 : Math.min(8, s.length - i - 1);
  return fmtNum(n, Math.max(2, dec));
}

// percentuale compatta: 22 → '22%', 4.5 → '4,5%'
function fmtPerc(x) {
  const n = Number(x);
  if (x == null || x === '' || isNaN(n)) return '';
  return String(n).replace('.', ',') + '%';
}

// 'YYYY-MM-DD' → 'gg/mm/aaaa'
function fmtData(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[3] + '/' + m[2] + '/' + m[1] : iso;
}

function str(v) { return v == null ? '' : String(v); }

// ————————————————————————— HELPER RECORD ————————————————————————————————————

// denominazione XOR nome+cognome (contratto)
function nomeSoggetto(a) {
  if (!a) return '';
  if (a.denominazione) return a.denominazione;
  return [a.titolo, a.nome, a.cognome].filter(Boolean).join(' ');
}

function rigaIndirizzo(sede) {
  if (!sede) return [];
  const out = [];
  const via = [sede.indirizzo, sede.numeroCivico].filter(Boolean).join(', ');
  if (via) out.push(via);
  const citta = [sede.cap, sede.comune, sede.provincia ? '(' + sede.provincia + ')' : '']
    .filter(Boolean).join(' ');
  const conNazione = (sede.nazione && sede.nazione !== 'IT')
    ? (citta ? citta + ' - ' + sede.nazione : sede.nazione) : citta;
  if (conNazione) out.push(conNazione);
  return out;
}

function numeroDocumento(record) {
  if (record.numero != null && record.numero !== '') {
    return str(record.numero) + (record.serie ? '/' + record.serie : '');
  }
  if (record.numeroCustom) return str(record.numeroCustom);
  return '(bozza)';
}

function scontiRigaLabel(sconti) {
  if (!Array.isArray(sconti) || !sconti.length) return '';
  return sconti.map((s) => {
    const segno = s.tipo === 'MG' ? '+' : '-';
    if (s.percentuale != null && s.percentuale !== '') return segno + fmtPerc(s.percentuale);
    return segno + fmtImporto(s.importo);
  }).join(' ');
}

// ————————————————————————— MOTORE DI IMPAGINAZIONE ———————————————————————————

// S = stato di disegno { doc, y, colonne, drawTableHead }
function nuovaPagina(S) {
  S.doc.addPage();
  S.y = MARGIN;
}

// garantisce spazio verticale; a pagina nuova ridisegna l'eventuale header tabella
function ensureSpace(S, h, ridisegnaHeadTabella) {
  if (S.y + h > BOTTOM_Y) {
    nuovaPagina(S);
    if (ridisegnaHeadTabella && S.drawTableHead) S.drawTableHead();
  }
}

function setInk(doc) { doc.setTextColor(INK); }
function setLabel(doc) { doc.setTextColor(GRAY_LABEL); }

// banda grigia con titolo in maiuscoletto
function banda(doc, x, y, w, titolo) {
  doc.setFillColor(GRAY_BAND);
  doc.setDrawColor(GRAY_LINE);
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, BAND_H, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FS.band);
  setInk(doc);
  doc.text(titolo, x + BOX_PAD, y + BAND_H - 1.6);
}

// ————————————————————————— SEZIONE 1: riquadri cedente/cessionario ———————————

function lineeCedente(c) {
  const out = [];
  if (!c) return out;
  const piva = c.partitaIva ? (str(c.idPaese || 'IT') + str(c.partitaIva)) : '';
  if (nomeSoggetto(c)) out.push({ t: nomeSoggetto(c), bold: true });
  if (piva) out.push({ t: 'P.IVA: ' + piva });
  if (c.codiceFiscale) out.push({ t: 'C.F.: ' + c.codiceFiscale });
  rigaIndirizzo(c.sede).forEach((r) => out.push({ t: r }));
  if (c.regimeFiscale) {
    out.push({ t: 'Regime fiscale: ' + c.regimeFiscale + (REGIME[c.regimeFiscale] ? ' - ' + REGIME[c.regimeFiscale] : '') });
  }
  if (c.contatti && (c.contatti.telefono || c.contatti.email)) {
    out.push({ t: [c.contatti.telefono ? 'Tel. ' + c.contatti.telefono : '', c.contatti.email].filter(Boolean).join(' - ') });
  }
  return out;
}

function lineeCessionario(c, record) {
  const out = lineeCedente(c ? Object.assign({}, c, { regimeFiscale: '', contatti: null }) : c);
  const sdi = str(record.codiceDestinatario);
  if (sdi) out.push({ t: 'Codice destinatario SDI: ' + sdi });
  if (record.pecDestinatario) out.push({ t: 'PEC: ' + record.pecDestinatario });
  return out;
}

function disegnaRiquadroSoggetto(S, x, w, titolo, linee) {
  const doc = S.doc;
  const y0 = S.y;
  const h = BAND_H + BOX_PAD * 2 + Math.max(1, linee.length) * LINE_H;
  banda(doc, x, y0, w, titolo);
  doc.setFontSize(FS.body);
  let ty = y0 + BAND_H + BOX_PAD + LINE_H - 1;
  linee.forEach((l) => {
    doc.setFont('helvetica', l.bold ? 'bold' : 'normal');
    setInk(doc);
    const chunk = doc.splitTextToSize(l.t, w - BOX_PAD * 2);
    doc.text(chunk, x + BOX_PAD, ty);
    ty += LINE_H * (Array.isArray(chunk) ? chunk.length : 1);
  });
  const hReale = Math.max(h, ty - y0 - LINE_H + BOX_PAD + 1);
  doc.setDrawColor(GRAY_LINE);
  doc.setLineWidth(0.2);
  doc.rect(x, y0, w, hReale, 'S');
  return hReale;
}

function sezioneIntestazione(S, record) {
  const w = (CONTENT_W - GAP) / 2;
  const h1 = disegnaRiquadroSoggetto(S, MARGIN, w, 'CEDENTE / PRESTATORE', lineeCedente(record.cedente));
  const h2 = disegnaRiquadroSoggetto(S, MARGIN + w + GAP, w, 'CESSIONARIO / COMMITTENTE', lineeCessionario(record.cessionario, record));
  S.y += Math.max(h1, h2) + GAP;
}

// ————————————————————————— SEZIONE 2: dati documento ————————————————————————

function sezioneDatiDocumento(S, record) {
  const doc = S.doc;
  const y0 = S.y;
  const h = 10.5;
  const celle = [
    { label: 'TIPOLOGIA DOCUMENTO', value: str(record.tipoDocumento) + (TIPO_DOC[record.tipoDocumento] ? ' - ' + TIPO_DOC[record.tipoDocumento] : ''), w: CONTENT_W - 30 - 26 - 20 },
    { label: 'NUMERO', value: numeroDocumento(record), w: 30 },
    { label: 'DATA', value: fmtData(record.dataEmissione), w: 26 },
    { label: 'DIVISA', value: str(record.divisa || 'EUR'), w: 20 },
  ];
  doc.setDrawColor(GRAY_LINE);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN, y0, CONTENT_W, h, 'S');
  let x = MARGIN;
  celle.forEach((c, i) => {
    if (i > 0) doc.line(x, y0, x, y0 + h);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FS.label);
    setLabel(doc);
    doc.text(c.label, x + BOX_PAD, y0 + 3.4);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FS.body);
    setInk(doc);
    doc.text(c.value, x + BOX_PAD, y0 + 8.4);
    x += c.w;
  });
  S.y = y0 + h + 2;

  // note sotto la riga documento: art.73 + causali
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FS.small);
  setInk(doc);
  if (record.art73) {
    doc.text('Documento emesso ai sensi dell\'art. 73 del DPR 633/72', MARGIN, S.y + LINE_H_SMALL - 1);
    S.y += LINE_H_SMALL;
  }
  const causali = (record.datiGenerali && Array.isArray(record.datiGenerali.causali))
    ? record.datiGenerali.causali.filter(Boolean) : [];
  if (causali.length) {
    const chunk = S.doc.splitTextToSize('Causale: ' + causali.join(' - '), CONTENT_W);
    doc.text(chunk, MARGIN, S.y + LINE_H_SMALL - 1);
    S.y += LINE_H_SMALL * (Array.isArray(chunk) ? chunk.length : 1);
  }
  S.y += GAP - 2;
}

// ————————————————————————— SEZIONE 3: tabella righe —————————————————————————

function costruisciColonne(conSconti) {
  const cols = [
    { key: 'n', label: 'N.', w: 8, align: 'left' },
    { key: 'descrizione', label: 'Descrizione', w: 0, align: 'left' },
    { key: 'quantita', label: 'Quantità', w: 16, align: 'right' },
    { key: 'um', label: 'UM', w: 11, align: 'left' },
    { key: 'prezzo', label: 'Prezzo unit.', w: 23, align: 'right' },
  ];
  if (conSconti) cols.push({ key: 'sconti', label: 'Sconti/Magg.', w: 19, align: 'right' });
  cols.push({ key: 'iva', label: 'IVA/Nat.', w: 15, align: 'right' });
  cols.push({ key: 'totale', label: 'Totale riga', w: 24, align: 'right' });
  const fisse = cols.reduce((s, c) => s + (c.key === 'descrizione' ? 0 : c.w), 0);
  cols[1].w = CONTENT_W - fisse;
  let x = MARGIN;
  cols.forEach((c) => { c.x = x; x += c.w; });
  return cols;
}

function testoCella(doc, valore, col, y) {
  if (valore == null || valore === '') return;
  if (col.align === 'right') doc.text(String(valore), col.x + col.w - 1.2, y, { align: 'right' });
  else doc.text(String(valore), col.x + 1.2, y);
}

function sezioneTabellaRighe(S, record) {
  const doc = S.doc;
  const righe = Array.isArray(record.righe) ? record.righe : [];
  const conSconti = righe.some((r) => Array.isArray(r.sconti) && r.sconti.length > 0);
  const cols = costruisciColonne(conSconti);
  const descW = cols[1].w - 2.4;

  const drawHead = () => {
    doc.setFillColor(GRAY_BAND);
    doc.setDrawColor(GRAY_LINE);
    doc.setLineWidth(0.2);
    doc.rect(MARGIN, S.y, CONTENT_W, TABLE_HEAD_H, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FS.small);
    setInk(doc);
    const ty = S.y + TABLE_HEAD_H - 1.9;
    cols.forEach((c) => testoCella(doc, c.label, c, ty));
    S.y += TABLE_HEAD_H;
  };
  S.drawTableHead = drawHead;

  ensureSpace(S, TABLE_HEAD_H + LINE_H + ROW_PAD_V * 2);
  drawHead();

  doc.setFontSize(FS.body);
  righe.forEach((r, idx) => {
    // testo descrizione (+ codici articolo, + periodo) con wrapping
    doc.setFont('helvetica', 'normal');
    let descTesto = str(r.descrizione);
    if (Array.isArray(r.codiciArticolo) && r.codiciArticolo.length) {
      descTesto += '\nCod. ' + r.codiciArticolo.map((c) => [c.tipo, c.valore].filter(Boolean).join(' ')).join(', ');
    }
    if (r.dataInizioPeriodo || r.dataFinePeriodo) {
      descTesto += '\nPeriodo: ' + fmtData(r.dataInizioPeriodo) + ' - ' + fmtData(r.dataFinePeriodo);
    }
    const descLinee = doc.splitTextToSize(descTesto, descW);
    const nLinee = Math.max(1, Array.isArray(descLinee) ? descLinee.length : 1);
    const rowH = nLinee * LINE_H + ROW_PAD_V * 2;

    ensureSpace(S, rowH, true);
    // la testata (bold, font piccolo) può essere appena stata ridisegnata:
    // si ripristina SEMPRE il font di corpo prima della riga
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FS.body);

    if (idx % 2 === 1) { // zebratura leggera
      doc.setFillColor(GRAY_ZEBRA);
      doc.rect(MARGIN, S.y, CONTENT_W, rowH, 'F');
    }
    setInk(doc);
    const baseY = S.y + ROW_PAD_V + LINE_H - 1;
    testoCella(doc, str(r.numeroLinea != null ? r.numeroLinea : idx + 1), cols[0], baseY);
    doc.text(descLinee, cols[1].x + 1.2, baseY);
    const perCol = {
      quantita: r.quantita == null ? '' : fmtNum(r.quantita, 2),
      um: str(r.unitaMisura),
      prezzo: fmtPrezzo(r.prezzoUnitario),
      sconti: scontiRigaLabel(r.sconti),
      iva: r.natura ? str(r.natura) : fmtPerc(r.aliquotaIva),
      totale: fmtImporto(r.prezzoTotale),
    };
    cols.forEach((c) => {
      if (c.key === 'n' || c.key === 'descrizione') return;
      testoCella(doc, perCol[c.key], c, baseY);
    });
    S.y += rowH;
    doc.setDrawColor(GRAY_LINE);
    doc.setLineWidth(0.1);
    doc.line(MARGIN, S.y, MARGIN + CONTENT_W, S.y);
  });

  S.drawTableHead = null;
  S.y += GAP;
}

// ————————————————————————— SEZIONE 4: riepiloghi IVA + totali ————————————————

function righeTotali(record) {
  const dg = record.datiGenerali || {};
  const out = [
    { label: 'Totale imponibile', value: fmtImporto(record.totaleImponibile) },
    { label: 'Totale imposta', value: fmtImporto(record.totaleImposta) },
  ];
  if (dg.bollo && dg.bollo.importo != null) {
    out.push({ label: 'Bollo virtuale', value: fmtImporto(dg.bollo.importo) });
  }
  if (dg.arrotondamento != null && Number(dg.arrotondamento) !== 0) {
    out.push({ label: 'Arrotondamento', value: fmtImporto(dg.arrotondamento) });
  }
  out.push({ label: 'Totale documento', value: fmtImporto(record.totaleDocumento), bold: true });
  if (Number(record.totaleRitenute) > 0) {
    const tipi = (dg.ritenute || []).map((r) => RITENUTA_TIPO[r.tipo] || r.tipo).filter(Boolean);
    out.push({ label: 'Ritenute' + (tipi.length ? ' (' + tipi.join(', ') + ')' : ''), value: '-' + fmtImporto(record.totaleRitenute) });
  }
  out.push({ label: 'NETTO A PAGARE', value: fmtImporto(record.nettoAPagare), finale: true });
  return out;
}

function sezioneRiepiloghiETotali(S, record) {
  const doc = S.doc;
  const riep = Array.isArray(record.riepiloghi) ? record.riepiloghi : [];
  const totali = righeTotali(record);

  const hRiep = BAND_H + TABLE_HEAD_H + Math.max(1, riep.length) * (LINE_H + ROW_PAD_V) + BOX_PAD;
  const hTot = BAND_H + totali.length * (LINE_H + 1.2) + BOX_PAD * 2;
  ensureSpace(S, Math.max(hRiep, hTot));
  const y0 = S.y;

  // — riquadro riepiloghi IVA (sinistra)
  banda(doc, MARGIN, y0, RIEP_W, 'RIEPILOGHI IVA');
  const rcols = [
    { label: 'Aliquota/Natura', w: RIEP_W * 0.34, align: 'left' },
    { label: 'Imponibile', w: RIEP_W * 0.24, align: 'right' },
    { label: 'Imposta', w: RIEP_W * 0.20, align: 'right' },
    { label: 'Esigibilità', w: RIEP_W * 0.22, align: 'left' },
  ];
  let x = MARGIN;
  rcols.forEach((c) => { c.x = x; x += c.w; });
  let ry = y0 + BAND_H;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FS.small);
  setLabel(doc);
  rcols.forEach((c) => testoCella(doc, c.label, c, ry + TABLE_HEAD_H - 2.2));
  ry += TABLE_HEAD_H;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FS.body);
  setInk(doc);
  riep.forEach((r) => {
    ry += LINE_H + ROW_PAD_V;
    const valori = [
      r.natura ? str(r.natura) + (r.riferimentoNormativo ? ' - ' + r.riferimentoNormativo : '') : fmtNum(r.aliquotaIva, 2) + '%',
      fmtImporto(r.imponibile),
      fmtImporto(r.imposta),
      r.esigibilita ? str(r.esigibilita) + ' - ' + (ESIGIBILITA[r.esigibilita] || '') : '',
    ];
    rcols.forEach((c, i) => {
      const testo = doc.splitTextToSize(String(valori[i] == null ? '' : valori[i]), c.w - 2.4);
      const primo = Array.isArray(testo) ? testo[0] : testo; // una riga per compattezza
      testoCella(doc, primo, c, ry - 1);
    });
  });
  const hRiepReale = Math.max(hRiep, ry + BOX_PAD - y0);
  doc.setDrawColor(GRAY_LINE);
  doc.setLineWidth(0.2);
  doc.rect(MARGIN, y0, RIEP_W, hRiepReale, 'S');

  // — blocco totali (destra)
  const tx = MARGIN + RIEP_W + GAP;
  banda(doc, tx, y0, TOTALI_W, 'TOTALI');
  let ty = y0 + BAND_H + BOX_PAD;
  totali.forEach((r) => {
    ty += LINE_H + 1.2;
    if (r.finale) {
      doc.setFillColor(GRAY_BAND);
      doc.rect(tx, ty - LINE_H - 0.8, TOTALI_W, LINE_H + 2.2, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FS.totaleFinale);
    } else {
      doc.setFont('helvetica', r.bold ? 'bold' : 'normal');
      doc.setFontSize(FS.body);
    }
    setInk(doc);
    doc.text(r.label, tx + BOX_PAD, ty - 1);
    doc.text(r.value + (r.finale ? ' ' + str(record.divisa || 'EUR') : ''), tx + TOTALI_W - BOX_PAD, ty - 1, { align: 'right' });
  });
  const hTotReale = Math.max(hTot, ty + BOX_PAD - y0);
  doc.rect(tx, y0, TOTALI_W, hTotReale, 'S');

  S.y = y0 + Math.max(hRiepReale, hTotReale) + GAP;
}

// ————————————————————————— SEZIONE 5: pagamento —————————————————————————————

function sezionePagamento(S, record) {
  const pag = record.pagamento;
  const rate = (pag && Array.isArray(pag.rate)) ? pag.rate : [];
  if (!pag || (!pag.condizioni && !rate.length)) return;
  const doc = S.doc;

  const condLabel = pag.condizioni
    ? str(pag.condizioni) + (CONDIZIONI_PAG[pag.condizioni] ? ' - ' + CONDIZIONI_PAG[pag.condizioni] : '') : '';
  const titolo = 'PAGAMENTO' + (condLabel ? '  ·  ' + condLabel : '');

  // pre-calcola i testi (il wrapping dipende dal font corrente)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FS.body);
  const testi = rate.map((rata, i) => {
    const mod = rata.modalita
      ? (MODALITA_PAG[rata.modalita] ? MODALITA_PAG[rata.modalita] + ' (' + rata.modalita + ')' : str(rata.modalita)) : '';
    const pezzi = [
      rate.length > 1 ? 'Rata ' + (i + 1) : 'Pagamento',
      mod,
      rata.dataScadenza ? 'scadenza ' + fmtData(rata.dataScadenza) : '',
      rata.importo != null ? 'importo ' + fmtImporto(rata.importo) + ' ' + str(record.divisa || 'EUR') : '',
      rata.iban ? 'IBAN ' + rata.iban : '',
      rata.istituto ? str(rata.istituto) : '',
    ].filter(Boolean);
    const chunk = doc.splitTextToSize(pezzi.join('  ·  '), CONTENT_W - BOX_PAD * 2);
    return Array.isArray(chunk) ? chunk : [chunk];
  });

  // segmenti: banda + rate; se una rata non ci sta si chiude il riquadro
  // e si riapre (con la stessa banda) sulla pagina successiva
  let segY0;
  let py;
  const apriSegmento = () => {
    banda(doc, MARGIN, S.y, CONTENT_W, titolo);
    segY0 = S.y;
    py = S.y + BAND_H + BOX_PAD;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FS.body);
    setInk(doc);
  };
  const chiudiSegmento = () => {
    doc.setDrawColor(GRAY_LINE);
    doc.setLineWidth(0.2);
    doc.rect(MARGIN, segY0, CONTENT_W, py + BOX_PAD - segY0, 'S');
  };

  ensureSpace(S, BAND_H + BOX_PAD * 2 + LINE_H + 0.8);
  apriSegmento();
  testi.forEach((linee) => {
    const hRata = linee.length * LINE_H + 0.8;
    if (py + hRata + BOX_PAD > BOTTOM_Y) {
      chiudiSegmento();
      nuovaPagina(S);
      apriSegmento();
    }
    doc.text(linee, MARGIN + BOX_PAD, py + LINE_H - 1);
    py += hRata;
  });
  chiudiSegmento();
  S.y = py + BOX_PAD + GAP;
}

// ————————————————————————— FINITURE PER PAGINA (footer + filigrana) ——————————

const NOTA_CORTESIA = 'Copia di cortesia - il documento fiscale è l\'XML trasmesso al SDI';
const TESTO_FILIGRANA = 'ANTEPRIMA — NON VALIDA AI FINI FISCALI';

function finiturePagine(S, record, opts) {
  const doc = S.doc;
  const isBozza = record.stato === 'bozza' || !!(opts && opts.titolo);
  const n = doc.getNumberOfPages();
  for (let p = 1; p <= n; p += 1) {
    doc.setPage(p);
    // piè di pagina
    const fy = PAGE_H - MARGIN;
    doc.setDrawColor(GRAY_LINE);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, fy - 4.5, MARGIN + CONTENT_W, fy - 4.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FS.foot);
    setLabel(doc);
    doc.text(NOTA_CORTESIA, MARGIN, fy - 1);
    doc.text('Pagina ' + p + ' di ' + n, MARGIN + CONTENT_W, fy - 1, { align: 'right' });
    // filigrana diagonale su ogni pagina della bozza
    if (isBozza) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(FS.watermark);
      doc.setTextColor(WATERMARK_GRAY);
      doc.text(TESTO_FILIGRANA, PAGE_W / 2, PAGE_H / 2, { align: 'center', angle: 38 });
    }
  }
  // badge in testa alla prima pagina
  if (isBozza) {
    doc.setPage(1);
    const badge = (opts && opts.titolo) ? opts.titolo : 'ANTEPRIMA BOZZA';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FS.badge);
    doc.setTextColor(BADGE_RGB[0], BADGE_RGB[1], BADGE_RGB[2]);
    doc.setDrawColor(BADGE_RGB[0], BADGE_RGB[1], BADGE_RGB[2]);
    doc.setLineWidth(0.4);
    doc.rect(MARGIN, MARGIN - 8, 62, 6, 'S');
    doc.text(badge, MARGIN + 2.2, MARGIN - 3.6);
  }
  doc.setPage(n);
}

// ————————————————————————— API ———————————————————————————————————————————————

/**
 * Costruisce l'anteprima PDF di cortesia della fattura.
 * @param {Function} jsPDFCtor costruttore jsPDF (window.jspdf.jsPDF) — NON importato qui
 * @param {Object} record shape del contratto MVP (riepiloghi/totali già calcolati dal server)
 * @param {Object} [opts] { titolo?: 'ANTEPRIMA BOZZA' }
 * @returns {Object} istanza jsPDF pronta (il chiamante fa .output()/.save())
 */
export function buildFatturaPdf(jsPDFCtor, record, opts) {
  const rec = record || {};
  const doc = new jsPDFCtor({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const S = { doc, y: MARGIN, drawTableHead: null };

  sezioneIntestazione(S, rec);
  sezioneDatiDocumento(S, rec);
  sezioneTabellaRighe(S, rec);
  sezioneRiepiloghiETotali(S, rec);
  sezionePagamento(S, rec);
  finiturePagine(S, rec, opts);

  return doc;
}

export default buildFatturaPdf;
